/**
 * Shared domain types for the HUMINT workspace prototypes (PRD-0032, docs/domain/humint.md).
 *
 * These are VIEW types: what a pure component needs to render. They carry no API shapes
 * and no behaviour. Times are ISO-8601 strings, durations and transcript offsets are
 * milliseconds, image boxes and keypoints are normalised to 0..1 of their image.
 * A match is always a lead a person confirms, never an identification.
 */
import type { NormalizedBox } from '@/components/faces/highlight'
import type { MatchCondition } from '@/components/faces/match-tier'
import type { VoiceSampleQuality } from './voice/voice-sample-quality'

/** Normalised rectangle, 0..1 of the image width / height (the faces/highlight shape). */
export type { NormalizedBox }

// ── Workspace ────────────────────────────────────────────────────────────────

/** Top-level sections of the workspace shell (bottom tabs on a phone, left nav on a desktop). */
export type HumintSection =
  | 'home'
  | 'subjects'
  | 'encounters'
  | 'check'
  | 'sessions'
  | 'tattoos'
  | 'more'

/** The operator's membership in the active case (SC-036). */
export type CaseRole = 'viewer' | 'collaborator' | 'owner'

export interface CaseSummary {
  id: string
  name: string
  subjectCount: number
  faceCount: number
  unassignedFaceCount: number
  role: CaseRole
}

export interface OperatorSummary {
  /** Full display name with rank, e.g. "npor. Tomáš Hudec". */
  name: string
  initials: string
  unit?: string
}

/**
 * Why a list did not load: no connection, the read could not be recorded in the audit trail (so
 * the server returned nothing), or anything else.
 */
export type ListReadProblem = 'offline' | 'audit_unavailable' | 'error'

/** A list read that failed; `retrying` while it is read again: the notice stays, its button waits. */
export interface ListReadFailure {
  status: 'failed'
  problem: ListReadProblem
  retrying: boolean
}

// ── Subject and encounter ────────────────────────────────────────────────────

export type EncounterType =
  | 'detention'
  | 'checkpoint'
  | 'debriefing'
  | 'engagement'
  | 'source_meeting'

/** Selectable encounter types; `source_meeting` is reserved for later (shown disabled, "soon"). */
export const ENCOUNTER_TYPES: readonly EncounterType[] = [
  'detention',
  'checkpoint',
  'debriefing',
  'engagement',
  'source_meeting',
]

export type LegalStatus = 'prisoner_of_war' | 'civilian_internee' | 'other_detainee' | 'unknown'

/** Legal statuses a detention can record (GC III / GC IV first). */
export const LEGAL_STATUSES: readonly LegalStatus[] = [
  'prisoner_of_war',
  'civilian_internee',
  'other_detainee',
  'unknown',
]

/** Grammatical gender for "Neznámy / Neznáma" (i18n context), never shown as data. */
export type Sex = 'male' | 'female' | 'unknown'

export interface SubjectSummary {
  id: string
  /** Workspace number, e.g. "S-0213". Shown when there is no capture tag. */
  subjectNo: string
  /** Capture tag of the current detention (DD 2745 / CAPTAG), e.g. "ZA-0142". */
  captureTag?: string | null
  givenName?: string | null
  surname?: string | null
  nickname?: string | null
  /** Name as printed in its own script, e.g. "Олександр Мельник". */
  nameNative?: string | null
  sex: Sex
  portraitUrl?: string | null
  legalStatus?: LegalStatus | null
  latestEncounter?: { type: EncounterType; at: string } | null
  /**
   * When the workspace enrolled the subject, ISO. With it an unnamed subject without a capture
   * tag reads "Neznámy · 14:05 · 7f3a" (the time, then `subjectNo`).
   */
  enrolledAt?: string | null
  /**
   * Required steps of the person's record resolved, of all required ones (what all the
   * subject's encounters require); absent while there is no checklist.
   */
  checklist?: { done: number; total: number } | null
  /** Captures of this subject still uploading or failed (Home "pending uploads"). */
  pendingUploads?: number
  failedUploads?: number
  /**
   * Open tattoo leads touching the subject: its tattoos paired with another person's, waiting
   * for a decision or for a merge. Only the subject page and *Subjekty* say it; absent elsewhere.
   */
  tattooLeads?: number
  /**
   * Open voice leads touching the subject: its voiceprints paired with another person's, waiting
   * for a decision or for a merge. Only the subject page and *Subjekty* say it; absent elsewhere.
   */
  voiceLeads?: number
}

export interface GeoPosition {
  lat: number
  lon: number
  /** Military grid reference, e.g. "34U DV 48746 34496". */
  mgrs: string
  accuracyM?: number | null
  source: 'device' | 'manual'
}

export type CircumstanceId =
  | 'vehicle_check'
  | 'foot_patrol'
  | 'surrendered'
  | 'injured_treated'
  | 'other'

export interface EncounterSummary {
  id: string
  type: EncounterType
  at: string
  location?: GeoPosition | null
  placeNote?: string | null
  unit?: string | null
  circumstances: CircumstanceId[]
  circumstancesNote?: string | null
  /** Detention only. */
  captureTag?: string | null
  /** Detention only. */
  legalStatus?: LegalStatus | null
  /** Persons met together = the subjects of one encounter (SC-019). */
  subjectIds: string[]
  /** The check that led to this encounter, when there was one. */
  checkVerdictKind?: CheckVerdict['kind'] | null
}

// ── Captures and checklist ───────────────────────────────────────────────────

export type CaptureKind =
  | 'face_front'
  | 'face_45_left'
  | 'face_45_right'
  | 'face_profile_left'
  | 'face_profile_right'
  | 'full_body'
  | 'id_front'
  | 'id_back'
  | 'mark_orientation'
  | 'mark_closeup'
  | 'mark_scale'
  | 'audio'
  | 'voice_sample'

/**
 * Every capture kind, in checklist order: face views, body, document, mark shots, audio, and the
 * voice sample (ADR-0062), which is no step of the record.
 */
export const CAPTURE_KINDS: readonly CaptureKind[] = [
  'face_front',
  'face_45_left',
  'face_45_right',
  'face_profile_left',
  'face_profile_right',
  'full_body',
  'id_front',
  'id_back',
  'mark_orientation',
  'mark_closeup',
  'mark_scale',
  'audio',
  'voice_sample',
]

export type CaptureState =
  | 'pending'
  | 'uploading'
  | 'processing'
  | 'done'
  | 'no_face'
  /** A voice sample with too little speech for a voiceprint: the analogue of `no_face`. */
  | 'no_speech'
  | 'failed'
  | 'skipped'

export type SkipReason = 'refused' | 'injury' | 'time_or_safety' | 'other'

/** Reasons a capture step can be skipped with, in the order the chips show them. */
export const SKIP_REASONS: readonly SkipReason[] = ['refused', 'injury', 'time_or_safety', 'other']

/**
 * Where a capture's photo came from (ADR-0059). `imported`: from a file — the gallery, the file
 * dialog — not from the camera in the workspace; the capture's own time is then when it was added.
 */
export interface PhotoOrigin {
  imported: boolean
  /** When an imported photo says it was taken, ISO; null when it says nothing. */
  takenAt: string | null
}

export interface CaptureSummary extends Partial<PhotoOrigin> {
  id: string
  kind: CaptureKind
  state: CaptureState
  thumbnailUrl?: string | null
  /** 0..1 while uploading. */
  progress?: number | null
  capturedAt?: string | null
  skipReason?: SkipReason | null
  /**
   * A voice sample's quality as the server judged it (ADR-0062), once its voiceprint is kept;
   * null or absent until then, and for every other kind.
   */
  voiceQuality?: VoiceSampleQuality | null
}

/** One step of a person's record, the guided checklist (SC-016). */
export interface ChecklistStep {
  kind: CaptureKind
  /** False: a recommended step. It is asked for after the required ones and never counted. */
  required: boolean
  capture?: CaptureSummary | null
}

// ── Identity document ────────────────────────────────────────────────────────

export type IdDocumentType = 'id_card' | 'passport' | 'driving_licence' | 'military_id' | 'other'

// ── Face check ───────────────────────────────────────────────────────────────

/** Strength of a face or tattoo candidate. `strong` stays dormant until gate G4. */
export type MatchBand = 'strong' | 'possible' | 'weak'

/**
 * What the operator can fix in the next photo. `out_of_frame` = part of the face is cut off by
 * the edge; `unclear` = the detector was unsure it saw a face at all.
 */
export type QualityHint =
  | 'face_small'
  | 'blurry'
  | 'dark'
  | 'occluded'
  | 'off_angle'
  | 'multiple_faces'
  | 'out_of_frame'
  | 'unclear'

/**
 * One ADR-0040 condition of a suggested match, met or missed (shown as ✓ / ✗ with words,
 * SC-008). The ids are the face matcher's own: similarity, quality, agreement, margin.
 */
export interface CheckCondition {
  id: MatchCondition
  met: boolean
  /** Raw readout the component formats: reference count for `agreement` (1), margin to the next (0.06). */
  value?: number | null
}

export interface CheckScope {
  caseName: string
  faceCount: number
  checkedAt: string
  /** The audit record was written; a check without one is refused (SC-009). */
  audited: boolean
}

export interface CheckProbe {
  imageUrl: string
  /** Box of the face the check used (the largest one when there are several). */
  faceBox?: NormalizedBox | null
  faceCount: number
  qualityHints: QualityHint[]
}

export interface CheckCandidate {
  /** Face id. */
  id: string
  /** `unassigned_face` renders as "seen before, unnamed" and offers no enrol-from-face. */
  kind: 'subject' | 'unassigned_face'
  subject?: Pick<
    SubjectSummary,
    'id' | 'subjectNo' | 'captureTag' | 'givenName' | 'surname' | 'nickname' | 'sex'
  > | null
  /** The candidate's face crop; null until the check returns crops (the crop falls back to an icon). */
  faceImageUrl: string | null
  similarity: number
  band: MatchBand
  /** The face is a confirmed reference of the subject. */
  isReference: boolean
  /** The photo the candidate's face was found in; its name may be unknown. */
  source?: {
    documentId?: string | null
    documentName?: string | null
    encounterType?: EncounterType | null
    at?: string | null
  } | null
  conditions: CheckCondition[]
}

/**
 * Why a check gave no answer. `audit_unavailable`: the check could not be recorded, so it gives
 * no result (SC-009). `reembedding_pending`: the case's faces are being re-embedded for another
 * recognizer (ADR-0041); `recognizer_mismatch`: the faces service runs another recognizer than
 * the deployment expects. Neither is ever a *no match*.
 */
export type NotCheckedReason =
  | 'error'
  | 'timeout'
  | 'index_not_ready'
  | 'offline'
  | 'audit_unavailable'
  | 'service_unavailable'
  | 'recognizer_mismatch'
  | 'reembedding_pending'
export type RetakeReason = 'no_face' | 'face_too_small'

/** Verdict of a face check (SC-008). Never green, never "identified". */
export type CheckVerdict =
  | { kind: 'possible'; scope: CheckScope; probe: CheckProbe; candidates: CheckCandidate[] }
  | { kind: 'no_match'; scope: CheckScope; probe: CheckProbe; lowQuality: boolean }
  | { kind: 'retake'; probe: CheckProbe; reason: RetakeReason }
  | { kind: 'not_checked'; reason: NotCheckedReason; scope?: CheckScope | null }
  /** Dormant until gate G4 sets its thresholds; render with destructive surfaces. */
  | { kind: 'strong'; scope: CheckScope; probe: CheckProbe; candidates: CheckCandidate[] }

// ── Body map and marks ───────────────────────────────────────────────────────

export type BodyView = 'front' | 'back'

/** Always from the SUBJECT'S perspective. */
export type Laterality = 'left' | 'right' | 'center'

export type BodyRegionGroup = 'head_neck' | 'torso' | 'arm' | 'leg'

/** Stable region ids of body-map/body-map-regions.ts (30 front, 32 back). */
export type BodyRegionId =
  | 'head_face'
  | 'neck_front'
  | 'shoulder_right'
  | 'shoulder_left'
  | 'chest_right'
  | 'chest_left'
  | 'upper_arm_front_right'
  | 'upper_arm_front_left'
  | 'elbow_front_right'
  | 'elbow_front_left'
  | 'forearm_front_right'
  | 'forearm_front_left'
  | 'wrist_right'
  | 'wrist_left'
  | 'hand_palm_right'
  | 'hand_palm_left'
  | 'abdomen_upper'
  | 'abdomen_lower'
  | 'hip_groin_right'
  | 'hip_groin_left'
  | 'thigh_front_right'
  | 'thigh_front_left'
  | 'knee_right'
  | 'knee_left'
  | 'shin_right'
  | 'shin_left'
  | 'ankle_front_right'
  | 'ankle_front_left'
  | 'foot_top_right'
  | 'foot_top_left'
  | 'head_back'
  | 'neck_back'
  | 'shoulder_back_right'
  | 'shoulder_back_left'
  | 'upper_back_right'
  | 'upper_back_left'
  | 'upper_arm_back_right'
  | 'upper_arm_back_left'
  | 'elbow_back_right'
  | 'elbow_back_left'
  | 'forearm_back_right'
  | 'forearm_back_left'
  | 'wrist_back_right'
  | 'wrist_back_left'
  | 'hand_back_right'
  | 'hand_back_left'
  | 'mid_back_right'
  | 'mid_back_left'
  | 'lower_back_right'
  | 'lower_back_left'
  | 'buttock_right'
  | 'buttock_left'
  | 'thigh_back_right'
  | 'thigh_back_left'
  | 'knee_back_right'
  | 'knee_back_left'
  | 'calf_right'
  | 'calf_left'
  | 'heel_ankle_back_right'
  | 'heel_ankle_back_left'
  | 'sole_right'
  | 'sole_left'

export type MarkKind = 'tattoo' | 'scar' | 'birthmark' | 'other'
export const MARK_KINDS: readonly MarkKind[] = ['tattoo', 'scar', 'birthmark', 'other']

/** The eight ANSI/NIST Type-10 SMT tattoo classes. */
export type Type10Class =
  | 'human'
  | 'animal'
  | 'plant'
  | 'flag'
  | 'object'
  | 'abstract'
  | 'symbol'
  | 'other'
export const TYPE10_CLASSES: readonly Type10Class[] = [
  'human',
  'animal',
  'plant',
  'flag',
  'object',
  'abstract',
  'symbol',
  'other',
]

/** Fixed ink-colour list (PRD-0032, body map and marks). */
export type InkColour = 'black' | 'grey' | 'red' | 'blue' | 'green' | 'other'
export const INK_COLOURS: readonly InkColour[] = ['black', 'grey', 'red', 'blue', 'green', 'other']

/** A point on the body figure, in BODY_VIEWBOX units (the spot the operator tapped). */
export interface BodyPoint {
  x: number
  y: number
}

/** `imported` and `takenAt` are those of the mark's newest photo. */
export interface MarkSummary extends Partial<PhotoOrigin> {
  id: string
  subjectId: string
  /** Per-subject pin number shown on the body map. */
  number: number
  kind: MarkKind
  view: BodyView
  regionId: BodyRegionId
  /** Where in the region the operator tapped; null = the region's pin point. */
  pin?: BodyPoint | null
  laterality: Laterality
  type10Class?: Type10Class | null
  colours: InkColour[]
  description?: string | null
  /** `auto` = VLM description the operator has not edited yet ("check it"). */
  descriptionSource?: 'operator' | 'auto' | null
  /** `not_available`: this deployment has no VLM to draft a description (SC-006). */
  autoDescription?: 'available' | 'not_available'
  /** Letters or numbers inked in the mark, exactly as they are. */
  inscription?: string | null
  closeupUrl?: string | null
  /**
   * Width / height of the tattoo's crop, the frame its matched points are normalised to; null
   * until the features step records it (the pair view measures the drawn image meanwhile).
   */
  cropAspect?: number | null
  orientationUrl?: string | null
  scaleUrl?: string | null
  /** The orientation shot was skipped, and why (SC-026). */
  orientationSkipReason?: SkipReason | null
  photoCount: number
  /** `low` = fine-line / faded: "low detail", capped at possible, text search still works. */
  featureQuality?: 'ok' | 'low' | null
  /** Tattoo candidates waiting for an analyst. */
  pendingCandidates: number
  capturedAt?: string | null
}

// ── Tattoo matching ──────────────────────────────────────────────────────────

/** Tattoo bands; `strong` is shown as possible until the tattoo eval gate. */
export type TattooBand = Extract<MatchBand, 'strong' | 'possible'>

/** One inlier pair, normalised 0..1 to each crop and already un-mirrored: [xa, ya, xb, yb]. */
export type KeypointPair = readonly [number, number, number, number]

export interface TattooPairEvidence {
  inliers: number
  /** Inlier ratio 0..1. */
  ratio: number
  /** Smaller inlier hull as a share of its crop, 0..1 ("coverage"). */
  spread: number
  rotationDeg: number
  scale?: number | null
  mirrored: boolean
  band: TattooBand
  /** Strengths stay provisional until the tattoo evaluation is done (SC-027). */
  provisional: boolean
  /** At most 60 inlier pairs. */
  points: readonly KeypointPair[]
}

export type TattooPairStatus = 'suggested' | 'confirmed' | 'rejected'

export interface TattooPair {
  id: string
  queryMarkId: string
  candidateMarkId: string
  evidence: TattooPairEvidence
  status: TattooPairStatus
  /** "Different persons, shared motif" was recorded. */
  motifOnly: boolean
  /** Owners share an encounter or a photo: never offered a merge (cannot-link). */
  seenTogether: boolean
}

// ── Questioning ──────────────────────────────────────────────────────────────

export type SessionKind = 'tactical_questioning' | 'screening' | 'debriefing' | 'interrogation'

export type SpeakerRole = 'questioner' | 'subject' | 'interpreter'

export interface TranscriptSegment {
  id: string
  startMs: number
  endMs: number
  speaker: SpeakerRole
  /** BCP-47 language of the spoken text, e.g. "sk", "uk". */
  language: string
  text: string
  /** Machine translation into the deployment language, when the line is in another one. */
  translation?: { language: string; text: string } | null
}

/** `unit` and `weapons` are the recording screen's quick labels next to name, place and other. */
export type BookmarkLabel =
  | 'name'
  | 'place'
  | 'person'
  | 'unit'
  | 'weapons'
  | 'time'
  | 'vehicle'
  | 'other'

export interface SessionBookmark {
  id: string
  atMs: number
  label: BookmarkLabel | null
}

/** Source reliability A–F. */
export type SourceReliability = 'A' | 'B' | 'C' | 'D' | 'E' | 'F'
/** Information credibility 1–6. */
export type InfoCredibility = 1 | 2 | 3 | 4 | 5 | 6

/** One grade per session (SC-033); `null` on a session = ungraded. */
export interface Grade {
  reliability: SourceReliability
  credibility: InfoCredibility
}

export interface SessionSummary {
  id: string
  subjectId: string
  kind: SessionKind
  startedAt: string
  durationMs: number
  /** BCP-47 language of the subject. */
  subjectLanguage: string
  interpreter?: string | null
  grade: Grade | null
}
